import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faAlarmSnooze } from "@fortawesome/pro-solid-svg-icons/faAlarmSnooze";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Dropdown from "antd/es/dropdown";
import Menu from "antd/es/menu";
import { useIntl } from "react-intl";

import Activity from "@mapmycustomers/shared/types/entity/Activity";
import FormLayout from "@mapmycustomers/shared/types/layout/FormLayout";

import { getPostponeMenuItems } from "@app/component/activity/utils/postpone";
import RoundButton from "@app/component/RoundButton";
import ButtonType from "@app/enum/ButtonType";
import { activityListViewAnalytics } from "@app/scene/activity/activityTableAnalytics";
import { bulkPostpone } from "@app/scene/activity/store/actions";
import { getBulkPostponeLoading } from "@app/scene/activity/store/selectors";
import { RootState } from "@app/store/rootReducer";
import { activityLayoutModel } from "@app/util/layout/impl";
import { parseApiDate } from "@app/util/parsers";

interface Props {
  bulkPostpone: typeof bulkPostpone.request;
  bulkPostponeLoading: boolean;
  className?: string;
  selected: Activity[];
}

const BulkToggleCompleteButton: React.FC<Props> = ({
  bulkPostpone,
  bulkPostponeLoading,
  className,
  selected,
}) => {
  const intl = useIntl();
  const onBulkPostpone = useCallback(
    (getPostponedDate: (dateToStart: Date | number) => Date, period: string) => {
      bulkPostpone({
        activities: selected.map((activity) => {
          const { id, endAt, startAt } = activity;
          const updated: {
            endAt?: Activity["endAt"];
            id: Activity["id"];
            layoutId: FormLayout["id"];
            startAt: Activity["startAt"];
          } = {
            id,
            endAt,
            layoutId: activityLayoutModel.getLayoutFor(activity).id,
            startAt: getPostponedDate(parseApiDate(startAt || "")).toISOString(),
          };

          if (endAt) {
            updated.endAt = getPostponedDate(parseApiDate(endAt)).toISOString();
          }

          return updated;
        }),
        period,
      });
    },
    [bulkPostpone, selected]
  );
  const menuItems = useMemo(
    () => getPostponeMenuItems(intl, false, onBulkPostpone),
    [intl, onBulkPostpone]
  );

  const handleClick = useCallback(() => {
    activityListViewAnalytics.clicked(["Checkbox to Select All", "Postpone"]);
  }, []);

  return (
    <Dropdown
      overlay={
        <Menu>
          {menuItems.map((item, index) => (
            <Menu.Item key={index} onClick={item.onClick}>
              {item.label}
            </Menu.Item>
          ))}
        </Menu>
      }
      trigger={["click"]}
    >
      <RoundButton
        className={className}
        hasMenu
        leftIcon={<FontAwesomeIcon icon={faAlarmSnooze} />}
        loading={bulkPostponeLoading}
        onClick={handleClick}
        type={ButtonType.SECONDARY}
      >
        {intl.formatMessage({
          id: "activity.listView.bulkPostpone.postpone",
          defaultMessage: "Postpone",
          description: "Label for bulk postpone button",
        })}
      </RoundButton>
    </Dropdown>
  );
};

const mapStateToProps = (state: RootState) => ({
  bulkPostponeLoading: getBulkPostponeLoading(state),
});

const mapDispatchToProps = {
  bulkPostpone: bulkPostpone.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(BulkToggleCompleteButton);
